/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 * Helper functions for parsing raw AAS handover document submodel data into structured HandoverDocument objects.
 *
 * Features:
 * - Parses complete AAS handover document submodels into a flat list of HandoverDocument objects
 * - Resolves document classifications, versions, languages, and digital file types
 * - Falls back gracefully when optional fields are absent
 */

import {Submodel, SubmodelElement} from '@product-passport/dpp/model';

import {HandoverDocument} from '../../../../../models/dpp/handover-document';
import {MultiLanguageText} from '../../../../../models/dpp/multi-language-text';

type AasElement = SubmodelElement & {value?: unknown; contentType?: string};

const HD_SEMANTIC_IDS = {
  DOCUMENTS: '0173-1#02-ABI500#003',
  DOCUMENT_CLASSIFICATIONS: '0173-1#02-ABI502#003',
  DOCUMENT_VERSIONS: '0173-1#02-ABI503#003',
  CLASS_ID: '0173-1#02-ABH996#003',
  CLASS_NAME: '0173-1#02-ABJ219#002',
  LANGUAGE: '0173-1#02-AAN468#008',
  VERSION: '0173-1#02-AAP003#005',
  TITLE: '0173-1#02-ABG940#003',
  STATUS_SET_DATE: '0173-1#02-ABI000#003',
  DIGITAL_FILES: '0173-1#02-ABK126#002',
} as const;

/**
 * Parses a raw AAS handover document submodel into a flat list of HandoverDocument objects.
 * @param submodel - The raw submodel data
 * @returns Array of parsed HandoverDocument objects
 */
export function parseHandoverDocumentsSubmodel(
  submodel: unknown,
): HandoverDocument[] {
  const sm = submodel as Submodel;
  if (!Array.isArray(sm?.submodelElements)) {
    return [];
  }

  const documentsElement = findElement(
    sm.submodelElements,
    HD_SEMANTIC_IDS.DOCUMENTS,
    'Documents',
  );
  if (!Array.isArray(documentsElement?.value)) {
    return [];
  }

  return (documentsElement.value as AasElement[]).flatMap((docEntry, index) =>
    parseDocumentEntry(index, docEntry),
  );
}

/**
 * Returns the first element whose idShort or primary semantic ID matches.
 * Semantic ID lookup takes priority; idShort is the fallback.
 */
function findElement(
  elements: AasElement[],
  semanticId: string,
  idShort?: string,
): AasElement | undefined {
  const bySemanticId = elements.find(
    (el) => el.semanticId?.keys?.[0]?.value === semanticId,
  );
  if (bySemanticId) {
    return bySemanticId;
  }
  if (idShort) {
    return elements.find((el) => el.idShort === idShort);
  }
  return undefined;
}

function getMultiLanguageTexts(element: AasElement): MultiLanguageText[] {
  if (Array.isArray(element.value) && element.value.length > 0) {
    return element.value as MultiLanguageText[];
  }
  if (Array.isArray(element.displayName) && element.displayName.length > 0) {
    return element.displayName;
  }
  return [];
}

function getPropertyValue(element: AasElement): string | undefined {
  if (typeof element.value === 'string' && element.value.length > 0) {
    return element.value;
  }
  return undefined;
}

function resolveFileType(element: AasElement): string {
  if (element.contentType) {
    const parts = element.contentType.split('/');
    return (parts[parts.length - 1] ?? '').toUpperCase();
  }
  return '';
}

function parseDocumentEntry(
  index: number,
  docEntry: AasElement,
): HandoverDocument[] {
  if (!Array.isArray(docEntry.value)) {
    return [];
  }
  const docChildren = docEntry.value as AasElement[];
  const category = resolveCategory(docChildren);

  const versionsEl = findElement(
    docChildren,
    HD_SEMANTIC_IDS.DOCUMENT_VERSIONS,
    'DocumentVersions',
  );
  if (!Array.isArray(versionsEl?.value)) {
    return [];
  }

  return (versionsEl.value as AasElement[]).flatMap((versionEntry) => {
    const doc = parseVersionEntry(index, docEntry, category, versionEntry);
    return doc ? [doc] : [];
  });
}

function resolveCategory(docChildren: AasElement[]): MultiLanguageText[] {
  const classificationsEl = findElement(
    docChildren,
    HD_SEMANTIC_IDS.DOCUMENT_CLASSIFICATIONS,
    'DocumentClassifications',
  );
  if (!Array.isArray(classificationsEl?.value)) {
    return [];
  }

  const firstClassification = (classificationsEl.value as AasElement[])[0];
  if (!Array.isArray(firstClassification?.value)) {
    return [];
  }

  const classFields = firstClassification.value as AasElement[];
  const classNameEl = findElement(
    classFields,
    HD_SEMANTIC_IDS.CLASS_NAME,
    'ClassName',
  );
  if (classNameEl) {
    const texts = getMultiLanguageTexts(classNameEl);
    if (texts.length) {
      return texts;
    }
  }

  const classIdEl = findElement(
    classFields,
    HD_SEMANTIC_IDS.CLASS_ID,
    'ClassId',
  );
  const classIdValue = classIdEl ? getPropertyValue(classIdEl) : undefined;
  return classIdValue ? [{language: 'en', text: classIdValue}] : [];
}

function parseVersionEntry(
  index: number,
  docEntry: AasElement,
  category: MultiLanguageText[],
  versionEntry: AasElement,
): HandoverDocument | null {
  if (!Array.isArray(versionEntry.value)) {
    return null;
  }
  const versionFields = versionEntry.value as AasElement[];

  const titleEl = findElement(versionFields, HD_SEMANTIC_IDS.TITLE, 'Title');
  const title = titleEl ? getMultiLanguageTexts(titleEl) : [];

  const versionEl = findElement(
    versionFields,
    HD_SEMANTIC_IDS.VERSION,
    'Version',
  );
  const version = versionEl ? (getPropertyValue(versionEl) ?? '') : '';

  const dateEl = findElement(
    versionFields,
    HD_SEMANTIC_IDS.STATUS_SET_DATE,
    'StatusSetDate',
  );
  const date = dateEl ? (getPropertyValue(dateEl) ?? '') : '';

  const id = docEntry.idShort;
  const language = resolveLanguage(versionFields);
  const type = resolveDocumentType(versionFields);

  return {id, index, title, category, language, type, date, version};
}

function resolveLanguage(versionFields: AasElement[]): string {
  const languageEl = findElement(
    versionFields,
    HD_SEMANTIC_IDS.LANGUAGE,
    'Language',
  );
  if (!languageEl) {
    return '';
  }
  if (typeof languageEl.value === 'string') {
    return languageEl.value;
  }
  if (!Array.isArray(languageEl.value)) {
    return '';
  }
  return (languageEl.value as AasElement[])
    .map((prop) => getPropertyValue(prop))
    .filter(Boolean)
    .join(', ');
}

function resolveDocumentType(versionFields: AasElement[]): string {
  const digitalFilesEl = findElement(
    versionFields,
    HD_SEMANTIC_IDS.DIGITAL_FILES,
    'DigitalFiles',
  );
  if (!Array.isArray(digitalFilesEl?.value)) {
    return '';
  }
  const firstFile = (digitalFilesEl.value as AasElement[])[0];
  return firstFile ? resolveFileType(firstFile) : '';
}
